/**
 * Champ 6: "add a 2nd color of this design".
 * 1) Product page block: a tick box that adds the same design in another color, same size,
 *    right after the theme adds the shirt.
 * 2) Cart offer: for carts with 1-2 tees, a one-tap "add it in another color" card inside the
 *    cart drawer and cart page. Config comes from the "2nd color cart offer" section.
 * The price drop itself comes from the store's automatic discount, not from this script.
 */
(() => {
  if (window.Champ6SecondColor) return;

  const root = window.Shopify?.routes?.root || '/';
  // Cart writes from our add-ons run one after another so they never race each other
  const queue = (task) => (window.__c6CartQueue = (window.__c6CartQueue || Promise.resolve()).then(task, task));

  const formatMoney = (cents, format) => {
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*\w+\s*\}\}/, `${withCommas}.${fraction}`);
  };
  const clean = (value) => String(value || '').replace(/\s*\(Back View\)/i, '').trim();
  const rank = (priority, value) => {
    const v = clean(value).toLowerCase();
    const i = priority.findIndex((p) => p && v.startsWith(p));
    return i === -1 ? 99 : i;
  };
  const cartSectionIds = () =>
    [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

  async function addToCart(items, source) {
    const sectionIds = cartSectionIds();
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const json = await response.json();
    if (json.status) throw new Error(json.description || json.message);
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const { CartAddEvent } = await import('@theme/events');
    document.dispatchEvent(
      new CartAddEvent(cart, String(items[0].id), { source, itemCount: items.length, sections: json.sections })
    );
    return cart;
  }

  /* ---------- 1) Product page block ---------- */
  class C6SecondColor extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-c6-sc-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent || '[]');
      this.colorIndex = Number(this.dataset.colorIndex);
      this.priority = (this.dataset.priority || '').split(',').map((s) => s.trim().toLowerCase());
      this.toggle = this.querySelector('[data-c6-sc-toggle]');
      this.select = this.querySelector('[data-c6-sc-color]');

      this.toggle?.addEventListener('change', () => this.classList.toggle('is-on', this.toggle.checked));
      // The whole card ticks the box, except the color dropdown, which ticks it on change
      this.querySelector('[data-c6-sc-card]')?.addEventListener('click', (event) => {
        if (event.target === this.toggle || event.target === this.select) return;
        this.toggle.checked = !this.toggle.checked;
        this.toggle.dispatchEvent(new Event('change'));
      });
      this.select?.addEventListener('change', () => {
        this.#renderPick();
        if (!this.toggle.checked) {
          this.toggle.checked = true;
          this.toggle.dispatchEvent(new Event('change'));
        }
      });

      this.onVariant = () => requestAnimationFrame(() => this.#refresh());
      document.addEventListener('variant:update', this.onVariant);
      this.#refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.onVariant);
    }

    get current() {
      const form = document.getElementById(this.dataset.formId);
      const id = Number(form?.querySelector('[name="id"]')?.value);
      return this.variants.find((v) => v.id === id) || this.variants.find((v) => v.a) || this.variants[0];
    }

    /** Variants of the same size/fit in the other colors, best suggestion first */
    #alternatives() {
      const cur = this.current;
      if (!cur) return [];
      return this.variants
        .filter((v) => v.a && v.o[this.colorIndex] !== cur.o[this.colorIndex])
        .filter((v) => v.o.every((value, i) => i === this.colorIndex || value === cur.o[i]))
        .sort((a, b) => rank(this.priority, a.o[this.colorIndex]) - rank(this.priority, b.o[this.colorIndex]));
    }

    #refresh() {
      const alts = this.#alternatives();
      this.toggleAttribute('hidden-offer', alts.length === 0);
      if (!this.select) return;
      const previous = this.select.value;
      this.select.innerHTML = '';
      alts.forEach((v) => {
        const option = document.createElement('option');
        option.value = String(v.id);
        option.textContent = clean(v.o[this.colorIndex]);
        this.select.appendChild(option);
      });
      const keep = alts.find((v) => clean(v.o[this.colorIndex]) === previous);
      if (keep) this.select.value = String(keep.id);
      this.#renderPick();
    }

    #renderPick() {
      const pick = this.variants.find((v) => String(v.id) === this.select?.value);
      if (!pick) return;
      const off = Number(this.dataset.off) || 0;
      const thumb = this.querySelector('[data-c6-sc-thumb]');
      if (thumb && pick.img) thumb.src = pick.img;
      const price = this.querySelector('[data-c6-sc-price]');
      const was = this.querySelector('[data-c6-sc-was]');
      if (price) price.textContent = `+${formatMoney(Math.round((pick.p * (100 - off)) / 100), this.dataset.moneyFormat)}`;
      if (was) was.textContent = off ? formatMoney(pick.p, this.dataset.moneyFormat) : '';
    }

    /** The variant to add, or null when the box isn't ticked */
    get pick() {
      if (!this.toggle?.checked || this.hasAttribute('hidden-offer')) return null;
      return this.variants.find((v) => String(v.id) === this.select?.value) || null;
    }
  }
  customElements.define('c6-second-color', C6SecondColor);

  document.addEventListener('cart:update', (event) => {
    const { data } = event.detail || {};
    if (data?.source !== 'product-form-component' || data?.didError) return;
    const block = [...document.querySelectorAll('c6-second-color')].find((el) => el.pick);
    if (!block) return;
    const pick = block.pick;
    const quantity = Math.max(1, Number(data.itemCount) || 1);
    block.toggle.checked = false;
    block.classList.remove('is-on');
    queue(() =>
      addToCart([{ id: pick.id, quantity, properties: { _offer: '2nd color' } }], 'c6-second-color').catch((error) =>
        console.error('[Champ6] 2nd color add failed', error)
      )
    );
  });

  /* ---------- 2) Cart drawer / cart page offer ---------- */
  const configEl = document.getElementById('C6CartOfferConfig');
  const config = configEl ? JSON.parse(configEl.textContent || '{}') : null;
  const productCache = new Map();
  let rendering = false;
  let lastOffer = null;

  async function loadProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)));
    }
    return productCache.get(handle);
  }

  async function findOffer(cart) {
    const types = (config.types || []).map((t) => t.toLowerCase());
    const tees = cart.items.filter((i) => !i.parent_relationship && types.includes(String(i.product_type).toLowerCase()));
    const teeCount = tees.reduce((sum, i) => sum + i.quantity, 0);
    if (!teeCount || teeCount > config.maxTees) return null;

    const priority = (config.priority || []).map((p) => p.toLowerCase());
    for (const line of tees) {
      const product = await loadProduct(line.handle);
      if (!product) continue;
      const ci = product.options.findIndex((o) => /colou?r/i.test(o.name || o));
      if (ci === -1) continue;
      const lineVariant = product.variants.find((v) => v.id === line.variant_id);
      if (!lineVariant) continue;
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      const alts = product.variants
        .filter((v) => v.available && !inCart.has(v.id) && v.options[ci] !== lineVariant.options[ci])
        .filter((v) => v.options.every((value, i) => i === ci || value === lineVariant.options[i]))
        .sort((a, b) => rank(priority, a.options[ci]) - rank(priority, b.options[ci]));
      if (alts.length) {
        const v = alts[0];
        return {
          variant: v,
          color: clean(v.options[ci]),
          design: clean(product.title.replace(/^Champ6\s*\|\s*/i, '').replace(/\s*-\s*New Drop!?$/i, '')),
          img: (v.featured_image && v.featured_image.src) || product.featured_image,
        };
      }
    }
    return null;
  }

  function offerNode(offer) {
    const off = Number(config.off) || 0;
    const price = Math.round((offer.variant.price * (100 - off)) / 100);
    const el = document.createElement('div');
    el.className = 'c6-cart-offer';
    el.dataset.c6CartOffer = String(offer.variant.id);
    const img = document.createElement('img');
    img.src = offer.img ? `${offer.img}${offer.img.includes('?') ? '&' : '?'}width=120` : '';
    img.alt = '';
    img.className = 'c6-cart-offer__img';
    const body = document.createElement('div');
    body.className = 'c6-cart-offer__body';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'c6-cart-offer__eyebrow';
    eyebrow.textContent = config.eyebrow;
    const text = document.createElement('p');
    text.className = 'c6-cart-offer__text';
    text.textContent = config.text.replace('{design}', offer.design).replace('{color}', offer.color);
    const priceRow = document.createElement('p');
    priceRow.className = 'c6-cart-offer__price';
    priceRow.innerHTML = `${off ? '<s></s> ' : ''}${config.perk ? '<span></span>' : ''}`;
    if (off) priceRow.querySelector('s').textContent = formatMoney(offer.variant.price, config.moneyFormat);
    if (config.perk) priceRow.querySelector('span').textContent = config.perk;
    const label = config.button.replace('{price}', formatMoney(price, config.moneyFormat));
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'c6-cart-offer__btn';
    button.textContent = label;
    button.addEventListener('click', () => {
      button.disabled = true;
      button.textContent = '…';
      queue(() =>
        addToCart([{ id: offer.variant.id, quantity: 1, properties: { _offer: '2nd color' } }], 'c6-cart-offer').catch((error) => {
          console.error('[Champ6] cart offer add failed', error);
          button.disabled = false;
          button.textContent = label;
        })
      );
    });
    body.append(eyebrow, text, priceRow, button);
    el.append(img, body);
    return el;
  }

  async function renderCartOffer() {
    if (!config?.enabled || rendering) return;
    const targets = [...document.querySelectorAll('cart-items-component')];
    if (!targets.length) return;
    rendering = true;
    try {
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      lastOffer = await findOffer(cart);
      targets.forEach((target) => {
        target.querySelectorAll('[data-c6-cart-offer]').forEach((n) => n.remove());
        const items = target.querySelector('.cart-items');
        if (lastOffer && items) items.after(offerNode(lastOffer));
      });
    } catch (error) {
      console.error('[Champ6] cart offer failed', error);
    } finally {
      rendering = false;
    }
  }

  // Put the card back if a cart re-render removed it, without refetching
  function restoreCartOffer() {
    if (!lastOffer) return;
    document.querySelectorAll('cart-items-component').forEach((target) => {
      const items = target.querySelector('.cart-items');
      if (items && !target.querySelector('[data-c6-cart-offer]')) items.after(offerNode(lastOffer));
    });
  }

  if (config?.enabled) {
    document.addEventListener('cart:update', () => setTimeout(renderCartOffer, 150));
    new MutationObserver(() => restoreCartOffer()).observe(document.body, { childList: true, subtree: true });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderCartOffer);
    else renderCartOffer();
  }

  window.Champ6SecondColor = { renderCartOffer };
})();
